import { useMemo, useState } from 'react';
import { Download, Mail, Phone, User } from 'lucide-react';
import { useToast } from '@/shared/contexts/ToastContext';
import type { LeadKind } from './mockDashboardData';
import type { LeadFeedRow } from './leadFeedMapper';
import { sortLeadsForFeed } from './leadFeedMapper';
import { downloadLeadFeedCsv } from './leadFeedCsv';
import { ACCOUNT_ACCENT } from './accountStyles';
import { propertyTypeCell } from './propertyTypeDisplay';

export interface LeadFeedTableProps {
  rows: LeadFeedRow[];
  isLoading?: boolean;
  error?: Error | null;
}

export const LeadFeedTable = ({
  rows,
  isLoading = false,
  error = null
}: LeadFeedTableProps) => {
  const { notify } = useToast();
  const [leadTypeFilter, setLeadTypeFilter] = useState<'all' | LeadKind>('all');
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState<25 | 50>(25);

  const filteredFeed = useMemo(
    () => sortLeadsForFeed(rows, leadTypeFilter),
    [rows, leadTypeFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredFeed.length / pageSize));
  const safePage = Math.min(page, totalPages - 1);
  const pageRows = useMemo(() => {
    const start = safePage * pageSize;
    return filteredFeed.slice(start, start + pageSize);
  }, [filteredFeed, safePage, pageSize]);

  if (error) {
    return (
      <div
        className={`rounded-xl p-6 ${ACCOUNT_ACCENT.cardSurface} text-sm text-rose-700`}
      >
        Could not load leads: {error.message}
      </div>
    );
  }

  /** Relative past from an ISO timestamp — spelled out (e.g. “2 months ago”) so “m” is not read as minutes. */
  const formatRelativePast = (iso?: string | null): string => {
    if (!iso) return '—';
    const ts = new Date(iso).getTime();
    if (!Number.isFinite(ts)) return '—';
    const days = Math.max(0, Math.floor((Date.now() - ts) / 86_400_000));
    if (days === 0) return 'today';
    if (days < 7) return days === 1 ? '1 day ago' : `${days} days ago`;
    if (days < 28) {
      const w = Math.max(1, Math.round(days / 7));
      return w === 1 ? '1 week ago' : `${w} weeks ago`;
    }
    const months = Math.max(1, Math.round(days / 30));
    return months === 1 ? '1 month ago' : `${months} months ago`;
  };

  const renderPaginationControls = () => (
    <div className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2">
      <div className="flex items-center gap-2">
        <span className="text-xs text-slate-600">Per page</span>
        <select
          value={pageSize}
          onChange={e => {
            const next = Number(e.target.value) === 50 ? 50 : 25;
            setPageSize(next as 25 | 50);
            setPage(0);
          }}
          className="rounded-md border border-slate-200 bg-white px-2 py-1 text-xs text-slate-900"
        >
          <option value={25}>25</option>
          <option value={50}>50</option>
        </select>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          disabled={safePage === 0}
          onClick={() => setPage(p => Math.max(0, p - 1))}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-40"
        >
          Prev
        </button>
        <span className="text-xs text-slate-500">
          Page {safePage + 1} of {totalPages}
        </span>
        <button
          type="button"
          disabled={safePage >= totalPages - 1}
          onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
          className="rounded border border-slate-200 bg-white px-2 py-1 text-xs text-slate-700 disabled:opacity-40"
        >
          Next
        </button>
      </div>
    </div>
  );

  return (
    <div
      className={`overflow-hidden rounded-xl border border-slate-200 bg-white ${ACCOUNT_ACCENT.cardSurface}`}
    >
      <div className="flex flex-col gap-4 border-b border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between md:p-5">
        <h2 className="text-lg font-semibold text-slate-900">
          Lead feed ({isLoading ? '…' : filteredFeed.length})
        </h2>
        <div className="flex flex-wrap items-center gap-2 md:gap-3">
          <select
            value={leadTypeFilter}
            onChange={e => {
              setLeadTypeFilter(e.target.value as 'all' | LeadKind);
              setPage(0);
            }}
            disabled={isLoading}
            className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-700 shadow-sm outline-none focus:ring-2 focus:ring-[#2b6cff]/30 disabled:opacity-50"
          >
            <option value="all">Lead type · All</option>
            <option value="buyer">Buyer</option>
            <option value="lease">Lease</option>
          </select>
          <button
            type="button"
            disabled={isLoading || filteredFeed.length === 0}
            onClick={() => {
              downloadLeadFeedCsv(filteredFeed);
              notify('success', 'CSV downloaded.');
            }}
            className="inline-flex items-center gap-1.5 rounded-lg bg-[#2b6cff] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2563eb] disabled:cursor-not-allowed disabled:opacity-50"
          >
            <Download className="h-4 w-4 opacity-90" strokeWidth={2} />
            Download
          </button>
        </div>
      </div>

      <div className="overflow-auto rounded-xl border border-slate-200 bg-white">
        {!isLoading && filteredFeed.length > 0 ? (
          <div className="border-b border-slate-100 p-3">
            {renderPaginationControls()}
          </div>
        ) : null}
        <table className="min-w-full text-left text-sm">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50 text-xs font-semibold uppercase tracking-wide text-slate-600">
              <th className="px-4 py-3 font-medium">Purchase date</th>
              <th className="px-4 py-3 font-medium">Age of lead</th>
              <th className="px-4 py-3 font-medium">Lead type</th>
              <th className="px-4 py-3 font-medium">Contact information</th>
              <th className="px-4 py-3 font-medium">Property of interest</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 text-slate-800">
            {isLoading ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-12 text-center text-slate-500"
                >
                  Loading leads…
                </td>
              </tr>
            ) : pageRows.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  className="px-4 py-12 text-center text-slate-500"
                >
                  No assigned leads yet. Add funds and set your territory to get
                  matched.
                </td>
              </tr>
            ) : (
              pageRows.map(row => (
                <tr
                  key={row.assignmentId}
                  className="bg-white hover:bg-slate-50/60"
                >
                  <td className="px-4 py-3 tabular-nums text-slate-500">
                    {formatRelativePast(row.purchasedAtIso)}
                  </td>
                  <td className="px-4 py-3 tabular-nums text-slate-500">
                    {formatRelativePast(row.leadCreatedAtIso)}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span
                        className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-semibold ${
                          row.leadType === 'buyer'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-emerald-100 text-emerald-800'
                        }`}
                      >
                        {row.leadType === 'buyer' ? 'Buyer' : 'Tenant'}
                      </span>
                      {row.isClearance ? (
                        <span className="inline-block rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wide text-amber-900">
                          Clearance
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div className="flex items-center gap-2 font-semibold">
                      <User className="h-3.5 w-3.5 text-slate-500" />
                      <span>{row.name}</span>
                    </div>
                    <div className="mt-1 flex items-center gap-2 text-slate-600">
                      <Mail className="h-3.5 w-3.5 text-slate-500" />
                      {row.email !== '—' ? (
                        <a
                          className="hover:underline"
                          href={`mailto:${row.email}`}
                        >
                          {row.email}
                        </a>
                      ) : (
                        <span>{row.email}</span>
                      )}
                    </div>
                    <div className="mt-1 flex items-center gap-2 tabular-nums text-slate-600">
                      <Phone className="h-3.5 w-3.5 text-slate-500" />
                      <span>{row.phone}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-slate-700">
                    <div>
                      MLS: {row.mlsNumber?.trim() ? row.mlsNumber : '—'}
                    </div>
                    <div className="mt-1">
                      Property type: {propertyTypeCell(row.propertyType)}
                    </div>
                    <div className="mt-1 text-slate-600">{row.address}</div>
                    <div className="mt-1">
                      Price:{' '}
                      {row.propertyPrice?.trim()
                        ? `$${row.propertyPrice}`
                        : '—'}
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {!isLoading && filteredFeed.length > 0 ? (
        <div className="border-t border-slate-100 p-4 md:px-5">
          {renderPaginationControls()}
        </div>
      ) : null}
    </div>
  );
};
